import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, createActor, setup } from "xstate";

export type T_ShellComposerSeed = {
  id: number;
  sourcePending: boolean;
  text: string;
};

export type T_ShellContext = {
  composerSeed: T_ShellComposerSeed | null;
  nextComposerSeedId: number;
};

export type T_ShellEvent =
  | { type: "OPEN_SETTINGS" }
  | { type: "CLOSE_SETTINGS" }
  | { type: "OPEN_ACCOUNT" }
  | { type: "OPEN_NOTIFICATIONS" }
  | { type: "OPEN_SUBSCRIPTION" }
  | { type: "OPEN_USAGE" }
  | { type: "BACK" }
  | { type: "SEED_PROMPT"; text: string }
  | { type: "PROMPT_USED"; seedId: number };

// Integrations is no longer a panel — brokerage info and connect live on the
// settings root (RAI-1061).
export type T_ShellSettingsPanel =
  | "root"
  | "account"
  | "notifications"
  | "subscription"
  | "usage";

const shellSetupTypes: SetupTypes<
  T_ShellContext,
  T_ShellEvent,
  Record<never, string>,
  string,
  undefined,
  unknown,
  EventObject,
  MetaObject
> = {};

/**
 * One source of truth for settings navigation and external prompt intents.
 * Expo Router owns the selected conversation and its Drawer navigator owns
 * drawer motion; neither concern is duplicated in this machine.
 *
 * This actor is module-owned so notification taps and pushed product screens
 * can send intents before the shell route mounts. If shell state ever needs to
 * be exposed through Jotai, use jotai-xstate rather than hand-rolling a bridge.
 */
export const shellMachine = setup({
  types: shellSetupTypes,
  actions: {
    applyPromptSeed: assign(({ context, event }) => {
      if (event.type !== "SEED_PROMPT") return {};
      const nextComposerSeedId = context.nextComposerSeedId + 1;
      return {
        composerSeed: {
          id: nextComposerSeedId,
          sourcePending: true,
          text: event.text,
        },
        nextComposerSeedId,
      };
    }),
    markPromptUsed: assign(({ context, event }) => {
      if (
        event.type !== "PROMPT_USED" ||
        context.composerSeed === null ||
        context.composerSeed.id !== event.seedId
      ) {
        return {};
      }
      return {
        composerSeed: {
          ...context.composerSeed,
          sourcePending: false,
          text: "",
        },
      };
    }),
  },
}).createMachine({
  id: "shell",
  initial: "closed",
  context: {
    composerSeed: null,
    nextComposerSeedId: 0,
  },
  on: {
    CLOSE_SETTINGS: ".closed",
    OPEN_ACCOUNT: ".settings.account",
    OPEN_NOTIFICATIONS: ".settings.notifications",
    OPEN_SETTINGS: ".settings.root",
    OPEN_SUBSCRIPTION: ".settings.subscription",
    OPEN_USAGE: ".settings.usage",
    PROMPT_USED: { actions: "markPromptUsed" },
    SEED_PROMPT: { actions: "applyPromptSeed" },
  },
  states: {
    closed: {},
    settings: {
      initial: "root",
      states: {
        root: {
          on: {
            BACK: "#shell.closed",
            OPEN_ACCOUNT: "account",
          },
        },
        account: {
          on: {
            BACK: "root",
            OPEN_NOTIFICATIONS: "notifications",
            OPEN_SUBSCRIPTION: "subscription",
            OPEN_USAGE: "usage",
          },
        },
        notifications: { on: { BACK: "account" } },
        subscription: { on: { BACK: "account" } },
        usage: { on: { BACK: "account" } },
      },
    },
  },
});

export const shellActor = createActor(shellMachine).start();

export function openShellSettings(panel: T_ShellSettingsPanel = "root"): void {
  const events = {
    account: { type: "OPEN_ACCOUNT" },
    notifications: { type: "OPEN_NOTIFICATIONS" },
    root: { type: "OPEN_SETTINGS" },
    subscription: { type: "OPEN_SUBSCRIPTION" },
    usage: { type: "OPEN_USAGE" },
  } as const;
  shellActor.send(events[panel]);
}

export function seedShellPrompt(text: string): void {
  shellActor.send({ type: "SEED_PROMPT", text });
}

export function getShellSettingsPanel(value: unknown): T_ShellSettingsPanel {
  if (typeof value === "object" && value !== null && "settings" in value) {
    const panel = value.settings;
    if (
      panel === "account" ||
      panel === "notifications" ||
      panel === "subscription" ||
      panel === "usage"
    ) {
      return panel;
    }
  }
  return "root";
}
